import { createDecorator } from "../../../../platform/instantiation/common/registry";
import type { Event } from "../../../../base/common/events";
import type { ObjectPathRef } from "../../../../platform/webgl/common/objectPathRef.js";
import type { MeshBakeOutputMode } from "./meshBaker.js";

/**
 * The bake groups a user has assembled for the page they are inspecting.
 *
 * A group is a RECIPE — which meshes belong together and what they should
 * become. That is worth keeping across a reload, so groups are stored per page.
 * The optimized meshes themselves are NOT: they are ephemeral view state, never
 * emitted as operations and never written to a document, so a reload brings the
 * groups back with their meshes as they were, one click from re-applying.
 *
 * This exists as a service rather than module state because a panel has to
 * OBSERVE it. The context menu and the panel are both entry points that invoke;
 * neither owns what a group is.
 */

export const IMeshBakeGroupService = createDecorator<IMeshBakeGroupService>("meshBakeGroupService");

/**
 * One mesh in a group, named twice.
 *
 * `handle` is the uuid and is exact — but three re-mints uuids when the glTF
 * reloads, so it only names things within a session. `path` names a PLACE and
 * survives a reload of the same page. Resolution tries the handle first and
 * reports which one matched, so a fallback can be surfaced rather than silently
 * taken (the same shape `objectHandle.ts` uses).
 */
export interface BakeGroupMember {
    readonly handle: string | null;
    /**
     * NULL when no path could be built, never an empty array: an empty path
     * RESOLVES — to the root — so storing one made an unlocatable mesh silently
     * come back as the scene itself. Every member of a group then pointed at the
     * same wrong object.
     */
    readonly path: ObjectPathRef | null;
    /**
     * The object's OWN name — what a row shows. A path would be truncated to
     * nothing at panel width, cutting off the only part that identifies it.
     */
    readonly label: string;
    /** A few ancestors, for the row's tooltip — where the mesh sits. */
    readonly context?: string;
}

export interface BakeGroup {
    readonly id: string;
    readonly name: string;
    readonly outputMode: MeshBakeOutputMode;
    /**
     * Triangle budget the baker opens with. Undefined means "decide from the
     * source" — the panel shows that suggestion, and only a number the user
     * actually typed is stored, so a group does not freeze a budget computed
     * from a scene that has since changed.
     */
    readonly targetTriangles?: number;
    /**
     * Folded shut in the panel. Persisted with the group: a 45-mesh group is
     * folded precisely because the user does not want to scroll past it, and
     * having to fold it again on every reload defeats the point.
     */
    readonly collapsed?: boolean;
    readonly members: readonly BakeGroupMember[];
}

/** `detached` = held by a merged bake, out of the scene graph but restorable. */
export type MemberRung = "handle" | "path" | "detached";

export interface ResolvedMember {
    readonly object: object;
    readonly rung: MemberRung;
}

export interface IMeshBakeGroupService {
    readonly _serviceBrand: undefined;

    /** Fires on any change to any group, including a reload restoring them. */
    readonly onDidChangeGroups: Event<void>;

    readonly groups: readonly BakeGroup[];

    getGroup(id: string): BakeGroup | undefined;

    /** Creates an empty group. `objects` seeds it, e.g. from the current selection. */
    createGroup(name?: string, objects?: readonly object[]): BakeGroup;

    /**
     * The group whose members are EXACTLY the meshes in `objects`, if one
     * exists. The create command reuses it instead of minting a twin — the
     * service itself allows cross-group duplicates. An empty mesh set matches
     * nothing.
     */
    findGroupWithSameMembers(objects: readonly object[]): BakeGroup | undefined;

    removeGroup(id: string): void;
    renameGroup(id: string, name: string): void;
    setOutputMode(id: string, mode: MeshBakeOutputMode): void;
    setCollapsed(id: string, collapsed: boolean): void;
    /** Pass undefined to go back to the suggestion. */
    setTargetTriangles(id: string, triangles: number | undefined): void;

    /**
     * Adds meshes to a group. Non-meshes and duplicates are skipped, and the
     * count that actually landed is returned so a drop can report "3 of 5 added"
     * instead of appearing to do nothing.
     */
    addToGroup(id: string, objects: readonly object[]): number;

    removeFromGroup(id: string, member: BakeGroupMember): void;

    /** Null when the mesh is gone — the panel shows the member as missing. */
    resolveMember(member: BakeGroupMember): ResolvedMember | null;
}

// #region Identity

/**
 * A group id that cannot collide with anything, in this session or a stored one.
 *
 * The id used to be `group-<counter>-<length>` and NEITHER half is stable: the
 * counter lives in memory and restarts at zero on every page load while the
 * groups are persisted, and the length moves as groups come and go. A fresh
 * session therefore minted ids a stored group already held, and Svelte's keyed
 * each throws on the duplicate instead of rendering.
 *
 * Random rather than "highest existing + 1": nothing then depends on having
 * read the other groups first, which is the assumption that broke it the first
 * time. The id is never shown — the group's NAME is what the user sees.
 */
export function newGroupId(): string {
    const random = globalThis.crypto?.randomUUID?.();
    if (random) return `group-${random.slice(0, 8)}`;
    // Insecure contexts and older hosts have no randomUUID; the chrome
    // extension runs on whatever page the user is looking at.
    return `group-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Repairs a stored list that already contains collisions.
 *
 * Without this the fix above only helps groups made from now on — ids already
 * written to storage would keep throwing on every load.
 *
 * The FIRST holder of an id keeps it: variant state and the live session are
 * keyed by group token, so renaming the group that owns a bake would strand the
 * result in the scene with nothing able to switch or discard it.
 */
export function withUniqueIds<T extends { readonly id: string }>(groups: readonly T[]): T[] {
    const seen = new Set<string>();
    let changed = false;
    const result = groups.map(group => {
        if (!seen.has(group.id)) {
            seen.add(group.id);
            return group;
        }
        let id = newGroupId();
        while (seen.has(id) || groups.some(other => other.id === id)) id = newGroupId();
        seen.add(id);
        changed = true;
        return { ...group, id };
    });
    return changed ? result : [...groups];
}

// #region Naming

/** Long enough to be recognisable, short enough for a 200px panel. */
const MAX_GROUP_NAME = 24;

/**
 * A name for a NEW group, taken from the meshes dropped into it.
 *
 * Creation only. A name the user can rename must not be rewritten every time
 * they add a mesh, and a group called "Group 2" that silently becomes
 * "bumper front" three drops later is worse than one that never changed.
 *
 * Null when the names share nothing: a name built from one of five unrelated
 * meshes is a lie about the other four, and the caller's "Group 3" is the more
 * honest answer.
 */
export function suggestGroupName(names: readonly string[]): string | null {
    const cleaned = names.map(name => name.trim()).filter(Boolean);
    if (!cleaned.length) return null;
    if (cleaned.length === 1) return short(words(cleaned[0]).join(" ")) || null;

    // What they all begin with, which is how these models are usually named
    // ("bumper_front001", "bumper_front004").
    const prefix = words(commonPrefix(cleaned)).join(" ");
    if (prefix.length >= 3) return short(prefix);

    // Otherwise a word MOST of them share — "front_window", "rear_window".
    const counts = new Map<string, number>();
    for (const name of cleaned) {
        for (const word of new Set(words(name))) {
            counts.set(word, (counts.get(word) ?? 0) + 1);
        }
    }
    let best: string | null = null;
    let bestCount = 0;
    for (const [word, count] of counts) {
        // Half is the bar: a word in two of five names describes nothing.
        if (count < Math.max(2, Math.ceil(cleaned.length / 2))) continue;
        if (count > bestCount || (count === bestCount && best !== null && word.length > best.length)) {
            best = word;
            bestCount = count;
        }
    }
    return best ? short(best) : null;
}

/** Splits on separators and camel humps, dropping index-like fragments. */
function words(value: string): string[] {
    return value
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .split(/[^A-Za-z0-9]+/)
        .map(part => part.replace(/\d+$/, ""))
        .filter(part => part.length > 1);
}

function commonPrefix(values: readonly string[]): string {
    let prefix = values[0];
    for (const value of values.slice(1)) {
        let i = 0;
        while (i < prefix.length && i < value.length && prefix[i] === value[i]) i++;
        prefix = prefix.slice(0, i);
        if (!prefix) break;
    }
    return prefix;
}

function short(value: string): string {
    return value.length > MAX_GROUP_NAME ? value.slice(0, MAX_GROUP_NAME).trimEnd() : value;
}
